import React, { useEffect, useRef, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { X } from 'lucide-react';
import stripePromise from '../lib/stripe';

const CheckoutForm = ({ amount, currency, onPaid, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError('');

    try {
      const { error: submitErr } = await elements.submit();
      if (submitErr) {
        setError(submitErr.message || 'Please check your card details.');
        setSubmitting(false);
        return;
      }

      const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (confirmErr) {
        setError(confirmErr.message || 'Payment could not be completed.');
        setSubmitting(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        await onPaid(paymentIntent.id);
      } else {
        setError('Payment did not complete.');
        setSubmitting(false);
      }
    } catch (err) {
      setError(
        err?.message ||
          'Your payment went through, but confirming it with the server failed. Refresh this page — it will be reconciled automatically.'
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      <PaymentElement />
      {error && (
        <div className="mt-4 break-words rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {error}
        </div>
      )}
      <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="w-full cursor-pointer rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handlePay}
          disabled={!stripe || submitting}
          className="w-full cursor-pointer rounded-xl border-0 bg-green-700 px-5 py-3 text-sm font-bold text-white transition hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
        >
          {submitting ? 'Processing…' : `Pay ${currency.toUpperCase()} $${amount}`}
        </button>
      </div>
    </>
  );
};

const PaymentModal = ({ title = 'Confirm & Pay', headline, fetchIntent, onClose, onPaid }) => {
  const [state, setState] = useState({
    loading: true,
    error: '',
    clientSecret: '',
    amount: 0,
    currency: 'aud',
  });

  // Callers pass `fetchIntent` as an inline arrow, so its identity changes on
  // every render of the page behind this modal — and that page re-renders on a
  // timer and on every socket update. Keeping it in a ref lets the effect below
  // run ONCE per open: Stripe's <Elements> cannot swap its clientSecret after
  // mount, so re-fetching would leave the mounted form bound to the first
  // intent while the server moved on to a newer one, and the confirm call would
  // then be rejected as belonging to a different request.
  const fetchIntentRef = useRef(fetchIntent);
  fetchIntentRef.current = fetchIntent;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await fetchIntentRef.current();
        if (cancelled) return;
        const data = response?.data || {};

        if (!data.clientSecret) {
          setState((current) => ({
            ...current,
            loading: false,
            error: 'This payment is already complete. Please close and refresh.',
          }));
          return;
        }

        const amount = data.deposit ?? data.balance ?? 0;
        setState({
          loading: false,
          error: '',
          clientSecret: data.clientSecret,
          amount,
          currency: data.currency || 'aud',
        });
      } catch (err) {
        if (!cancelled) {
          setState((current) => ({
            ...current,
            loading: false,
            error: err?.message || 'Failed to start payment.',
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally once per mount — see the ref above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center overflow-y-auto bg-slate-950/60 p-3 backdrop-blur-sm sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <section
        className="my-auto flex max-h-[calc(100dvh-1.5rem)] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:rounded-3xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-100 px-4 py-4 sm:px-6 sm:py-5">
          <h2 className="m-0 min-w-0 break-words text-base font-black text-slate-950 sm:text-lg">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close payment"
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50"
          >
            <X size={18} />
          </button>
        </header>

        <div className="min-h-0 overflow-y-auto p-4 sm:p-6">
          {headline && <p className="mb-5 mt-0 break-words text-sm leading-6 text-slate-600">{headline}</p>}

          {state.loading ? (
            <div className="flex items-center gap-3 rounded-2xl bg-slate-50 p-4 text-sm font-medium text-slate-500">
              <span className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-slate-200 border-t-green-700" />
              <span className="min-w-0">Preparing secure payment…</span>
            </div>
          ) : state.error ? (
            <div className="break-words rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {state.error}
            </div>
          ) : (
            <Elements stripe={stripePromise} options={{ clientSecret: state.clientSecret }}>
              <CheckoutForm
                amount={state.amount}
                currency={state.currency}
                onPaid={onPaid}
                onClose={onClose}
              />
            </Elements>
          )}
        </div>
      </section>
    </div>
  );
};

export default PaymentModal;
